import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { projectsAPI, customersAPI } from "../../../api/client";
import { PERMISSIONS } from "../../../constants/permissions";
import { usePermission } from "../../../hooks/usePermission";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import "../../../components/projects/Projects.css";
import {
  PROJECT_STATUS_OPTIONS,
  formatProjectStatus,
} from "../../../utils/formatProjectStatus";

const STATUS_OPTIONS = PROJECT_STATUS_OPTIONS;

const LIMIT = 10;

export default function ArchivedProjectsPage() {
  const canDelete = usePermission(PERMISSIONS.PROJECT_DELETE);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalProjects, setTotalProjects] = useState(0);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);

  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sortBy, setSortBy] = useState("updatedAt");

  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);

  const scrollBodyRef = useRef(null);
  const sentinelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const resp = await projectsAPI.getArchived({
        search: search || undefined,
        statuses: statuses.length ? statuses.join(",") : undefined,
        workflows: workflows.length ? workflows.join(",") : undefined,
        customers: customers.length ? customers.join(",") : undefined,
        sortBy,
        skip: 0,
        limit: LIMIT,
      });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      const total = Number(resp.data?.pagination?.total || 0);
      setProjects(items);
      setTotalProjects(total);
      setHasMore(items.length === LIMIT && items.length < total);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load archived projects");
    } finally {
      setLoading(false);
    }
  }, [search, statuses, workflows, customers, sortBy]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      const [workflowResult, customerResult] = await Promise.allSettled([
        projectsAPI.workflowOptions(),
        customersAPI.getAll(),
      ]);
      setWorkflowOptions(
        workflowResult.status === "fulfilled" && Array.isArray(workflowResult.value?.data)
          ? workflowResult.value.data.map((w) => w.name)
          : [],
      );
      setCustomerOptions(
        customerResult.status === "fulfilled" && Array.isArray(customerResult.value?.data)
          ? customerResult.value.data.map((c) => c.name)
          : [],
      );
    };
    loadFilterOptions();
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || loading) return;
    try {
      setLoadingMore(true);
      const resp = await projectsAPI.getArchived({
        search: search || undefined,
        statuses: statuses.length ? statuses.join(",") : undefined,
        workflows: workflows.length ? workflows.join(",") : undefined,
        customers: customers.length ? customers.join(",") : undefined,
        sortBy,
        skip: projects.length,
        limit: LIMIT,
      });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      const total = Number(resp.data?.pagination?.total || 0);
      setProjects((prev) => [...prev, ...items]);
      setTotalProjects(total);
      setHasMore(items.length === LIMIT && projects.length + items.length < total);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, loading, projects.length, search, statuses, workflows, customers, sortBy]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollBodyRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { root: container, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      document.querySelectorAll("details.multi-filter-dropdown[open]").forEach((el) => {
        if (!el.contains(event.target)) el.removeAttribute("open");
      });
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const toggleFilter = (setter, value) => {
    setter((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  };

  const getFilterLabel = (selected, options, fallback) => {
    if (!selected.length) return fallback;
    const labels = options
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
  };

  const getWorkflowFilterLabel = () => {
    if (!workflows.length) return "All Workflows";
    if (workflows.length <= 2) return workflows.join(", ");
    return `${workflows.slice(0, 2).join(", ")} +${workflows.length - 2}`;
  };

  const getCustomerFilterLabel = () => {
    if (!customers.length) return "All Customers";
    if (customers.length <= 2) return customers.join(", ");
    return `${customers.slice(0, 2).join(", ")} +${customers.length - 2}`;
  };

  const handleUnarchive = (project) => {
    setConfirm({
      title: "Unarchive Project",
      message: `Restore "${project.name}" from the archive?`,
      confirmLabel: "Unarchive",
      confirmClass: "btn-primary",
      onConfirm: async () => {
        try {
          await projectsAPI.unarchive(project.id);
          await load();
        } catch (err) {
          setError(err.response?.data?.message || "Failed to unarchive project");
        }
      },
    });
  };

  return (
    <div className="projects-page">
      <div className="projects-top-bar">
        <div className="page-header">
          <div>
            <Link className="back-link" to="/projects">← Back to Projects</Link>
            <h1 style={{ margin: "8px 0 0" }}>Archived Projects</h1>
          </div>
        </div>

        <div className="project-filters">
          <input
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <details className="multi-filter-dropdown">
            <summary>{getFilterLabel(statuses, STATUS_OPTIONS, "All Statuses")}</summary>
            <div className="multi-filter-menu">
              {STATUS_OPTIONS.map((option) => (
                <label key={option.value} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={statuses.includes(option.value)}
                    onChange={() => toggleFilter(setStatuses, option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </details>
          <details className="multi-filter-dropdown">
            <summary>{getWorkflowFilterLabel()}</summary>
            <div className="multi-filter-menu">
              {workflowOptions.map((name) => (
                <label key={name} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={workflows.includes(name)}
                    onChange={() => toggleFilter(setWorkflows, name)}
                  />
                  {name}
                </label>
              ))}
            </div>
          </details>
          <details className="multi-filter-dropdown">
            <summary>{getCustomerFilterLabel()}</summary>
            <div className="multi-filter-menu">
              {customerOptions.map((name) => (
                <label key={name} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={customers.includes(name)}
                    onChange={() => toggleFilter(setCustomers, name)}
                  />
                  {name}
                </label>
              ))}
            </div>
          </details>
          <button
            className="btn-secondary btn-sm"
            onClick={() =>
              setSortBy((current) => {
                if (current === "updatedAt") return "name";
                if (current === "name") return "nameDesc";
                return "updatedAt";
              })
            }
            style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
          >
            {sortBy === "updatedAt" ? "Sort: Latest" : sortBy === "name" ? "Sort: A-Z" : "Sort: Z-A"}
          </button>
          <button
            className="btn-secondary btn-sm"
            onClick={() => { setSearch(""); setStatuses([]); setWorkflows([]); setCustomers([]); }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="projects-scroll-body" ref={scrollBodyRef}>
        {error && (
          <div className="small-muted" style={{ color: "#b91c1c" }}>{error}</div>
        )}
        {loading ? (
          <div className="small-muted">Loading archived projects...</div>
        ) : (
          <div className="projects-list">
            {projects.length === 0 && (
              <div className="small-muted">No archived projects found.</div>
            )}
            {projects.map((project) => (
              <article key={project.id} className="project-card flex justify-center">
                <div className="project-img-placeholder">No Image</div>
                <div className="project-content">
                  <div className="project-title-row">
                    <h3 style={{ margin: 0 }}>{project.name}</h3>
                  </div>
                  <div className="project-meta-chips">
                    {project.customer && (
                      <span className="folder-membership-chip">
                        Customer: {project.customer.name}
                      </span>
                    )}
                    {project.workflow && (
                      <span className="project-meta-chip">
                        Workflow: {project.workflow.name}
                      </span>
                    )}
                    {project.allocatedUsers?.length > 0 && (
                      <span className="project-meta-chip">
                        Allocated:{" "}
                        {project.allocatedUsers
                          .map((u) => `${u.firstName} ${u.lastName}`)
                          .join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="project-right">
                  <div className={`status-badge status-${project.status}`}>
                    {formatProjectStatus(project.status)}
                  </div>
                  <div className="project-actions">
                    <Link className="btn-secondary" to={`/projects/${project.id}`}>
                      View
                    </Link>
                    {canDelete && (
                      <button
                        className="btn-primary"
                        onClick={() => handleUnarchive(project)}
                      >
                        Unarchive
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        <div ref={sentinelRef} />
        {loadingMore && (
          <div className="small-muted" style={{ textAlign: "center", padding: "12px 0" }}>
            Loading more...
          </div>
        )}
        {!loading && !loadingMore && totalProjects > 0 && (
          <div className="small-muted" style={{ textAlign: "center", padding: "8px 0" }}>
            Showing {projects.length} of {totalProjects} archived projects
          </div>
        )}
      </div>

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onConfirm={() => {
            const action = confirm.onConfirm;
            setConfirm(null);
            action();
          }}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
